'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { Offer } from '@/services/dataTypes'
import { apiAddComment } from '@/apis/offers'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol } from '@/constants/hooks'
import { toast } from 'react-toastify'
import { X, Clock, Copy, Check, ExternalLink, ShieldCheck, Tag, Star, MessageSquare, Send, ThumbsUp, ThumbsDown, Sparkles, ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react'

interface ProductCommentModalProps {
    product: Offer
    merchantName: string
    merchantLogo: string
    domain: string
    isOpen: boolean
    onClose: () => void
    productImg?: string | null
    companyId?: string
}

const resolveImageUrl = (domain: string, src?: string | null, fallback?: string | null): string => {
    const raw = src || fallback || ''
    if (!raw) return ''
    if (/^https?:\/\//i.test(raw)) return raw
    if (raw.startsWith('data:')) return raw
    return getBaseImageUrl(domain, raw, '')
}

const ProductCommentModal = ({
    product,
    merchantName,
    merchantLogo,
    domain,
    isOpen,
    onClose,
    productImg,
    companyId
}: ProductCommentModalProps) => {
    const [mounted, setMounted] = useState(false)
    const [copied, setCopied] = useState(false)
    const [dealLoading, setDealLoading] = useState(false)
    const [helpfulVote, setHelpfulVote] = useState<'yes' | 'no' | null>(null)
    const [isExpanded, setIsExpanded] = useState(false)

    // Rating & Comment state
    const [rating, setRating] = useState<number>(0)
    const [hoverRating, setHoverRating] = useState<number>(0)
    const [commentText, setCommentText] = useState<string>('')
    const [submitting, setSubmitting] = useState(false)
    const [hasSubmitted, setHasSubmitted] = useState(false)

    const rawProductImg =
        productImg ||
        product?.product_image ||
        (product as any)?.offer?.product_image ||
        (product as any)?.image ||
        ''

    const fallbackMerchantImg =
        merchantLogo ||
        product?.merchant?.merchant_logo ||
        (product as any)?.offer?.merchant?.merchant_logo ||
        ''

    const [imageSrc, setImageSrc] = useState<string>(() =>
        resolveImageUrl(domain, rawProductImg, fallbackMerchantImg)
    )
    const [imgError, setImgError] = useState(false)

    useEffect(() => {
        setImgError(false)
        const primary = resolveImageUrl(domain, rawProductImg, '')
        const fallback = resolveImageUrl(domain, fallbackMerchantImg, '')
        setImageSrc(primary || fallback)
    }, [domain, rawProductImg, fallbackMerchantImg])

    const handleImageError = () => {
        const fallback = resolveImageUrl(domain, fallbackMerchantImg, '')
        if (fallback && imageSrc !== fallback) {
            setImageSrc(fallback)
        } else {
            setImgError(true)
        }
    }

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            const handleEsc = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose()
            }
            window.addEventListener('keydown', handleEsc)
            return () => {
                document.body.style.overflow = 'unset'
                window.removeEventListener('keydown', handleEsc)
            }
        } else {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose])

    useEffect(() => {
        if (isOpen && product?.unique_id) {
            setCopied(false)
            setDealLoading(false)
            setHelpfulVote(null)
            setRating(0)
            setHoverRating(0)
            setCommentText('')

            const lastRated = localStorage.getItem(`hasRated_${product.unique_id}`)
            setHasSubmitted(!!(lastRated && new Date().getTime() - parseInt(lastRated, 10) < 86400000))
        }
    }, [isOpen, product?.unique_id])

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        try {
            await navigator.clipboard.writeText(product.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            const el = document.createElement('textarea')
            el.value = product.coupon_code
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const handleHelpful = (type: 'yes' | 'no') => {
        setHelpfulVote(type)
        toast.success(type === 'yes' ? 'Thanks! Marked as working.' : 'Feedback recorded.', { autoClose: 2000 })
    }

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (hasSubmitted) {
            toast.error('You have already submitted a review recently.', { autoClose: 2500 })
            return
        }
        if (!rating && !commentText.trim()) {
            toast.error('Please select a star rating or write a review.', { autoClose: 2500 })
            return
        }

        setSubmitting(true)
        try {
            const offerId = product?.unique_id?.toString() || ''
            const compId = companyId || product?.company_id?.toString() || ''
            if (offerId && compId) {
                await apiAddComment(offerId, compId, commentText, rating.toString())
            }
            if (offerId) {
                localStorage.setItem(`hasRated_${offerId}`, new Date().getTime().toString())
            }
            setHasSubmitted(true)
            setCommentText('')
            setRating(0)
            toast.success('Thank you for your review!', { autoClose: 2500 })
        } catch {
            toast.error('Could not submit review. Please try again.', { autoClose: 2000 })
        } finally {
            setSubmitting(false)
        }
    }

    const finalUrl = product?.url?.startsWith('/') ? product.url.replace(/^\/+/, '') : (product?.url ?? '')
    const absoluteOutUrl = `/${finalUrl}`
    const hasCoupon = Boolean(product?.coupon_code)
    const offerDuration = product?.end_date ? calculateOfferDuration(product?.end_date) : null
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const hasDiscount = originalPrice > 0 && salePrice > 0 && originalPrice > salePrice
    const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const savings = hasDiscount ? (originalPrice - salePrice).toFixed(2) : '0'
    const currencySymbol = getCurrencySymbol(product?.currency)
    const cleanDetail = product?.offer_detail ? discardHTMLTags(product.offer_detail) : ''
    const isLongDetail = cleanDetail.length > 240 || (cleanDetail.match(/\n/g) || []).length >= 3

    if (!mounted || !isOpen || !product) return null

    const modalStyles = `
        @keyframes theme12ModalFade {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes theme12ModalPop {
            from {
                opacity: 0;
                transform: scale(0.96) translateY(14px);
            }
            to {
                opacity: 1;
                transform: scale(1) translateY(0);
            }
        }
        .theme12-custom-scroll::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
        }
        .theme12-custom-scroll {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
        }
    `

    const modalContent = (
        <>
            <style dangerouslySetInnerHTML={{ __html: modalStyles }} />
            <div
                className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 overflow-y-auto theme12-custom-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ animation: 'theme12ModalFade 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', willChange: 'transform', isolation: 'isolate', transform: 'translateZ(0)' }}
            >
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-slate-950/60 transition-opacity duration-300"
                    onClick={onClose}
                />

                {/* Modal Container */}
                <div
                    className="relative w-full max-w-2xl bg-white rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] border border-slate-200/80 overflow-hidden flex flex-col max-h-[90vh] z-10"
                    style={{ animation: 'theme12ModalPop 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Top Ambient Cover Wash */}
                    <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-br from-emerald-50/80 via-[#f8fafc]/50 to-transparent pointer-events-none -z-0" />

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-slate-100 text-slate-500 hover:text-slate-900 border border-slate-200/80 transition-all duration-200 shadow-xs hover:scale-105 active:scale-95 cursor-pointer"
                        aria-label="Close modal"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    {/* Product Showcase Banner (shown when product has a product image) */}
                    {rawProductImg && imageSrc && !imgError && (
                        <div className="relative bg-gradient-to-b from-slate-50 to-white/90 p-4 h-48 sm:h-56 flex items-center justify-center overflow-hidden border-b border-slate-100 shrink-0">
                            <Image
                                src={imageSrc}
                                alt={discardHTMLTags(product?.offer_title) || merchantName || 'Product'}
                                width={260}
                                height={190}
                                unoptimized={true}
                                className="object-contain w-full h-full max-h-44 p-2 transition-transform duration-300 hover:scale-105"
                                onError={handleImageError}
                            />
                            {discountPercent && (
                                <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white text-xs font-black rounded-full shadow-sm">
                                    <Tag size={11} /> {discountPercent}% OFF
                                </span>
                            )}
                        </div>
                    )}

                    {/* Header Bar */}
                    <div className="relative z-10 px-6 sm:px-8 pt-6 pb-3 shrink-0">
                        <div className="flex items-start gap-3.5 pr-8">
                            {/* Merchant Logo Avatar Box (or fallback avatar) */}
                            {fallbackMerchantImg && (
                                <div className={`relative shrink-0 ${rawProductImg ? 'w-11 h-11' : 'w-16 h-16 sm:w-20 sm:h-20'} bg-white rounded-2xl p-1.5 shadow-xs border border-slate-100 flex items-center justify-center overflow-hidden`}>
                                    <Image
                                        src={resolveImageUrl(domain, fallbackMerchantImg, '')}
                                        alt={merchantName || 'Brand'}
                                        fill
                                        className="object-contain p-1"
                                        unoptimized
                                    />
                                </div>
                            )}

                            {/* Badges & Title */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                    {merchantName && (
                                        <span className="text-[11px] font-bold text-[#00a87a] uppercase tracking-wider">
                                            {merchantName}
                                        </span>
                                    )}
                                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 bg-emerald-50 text-emerald-800 rounded-md border border-emerald-200">
                                        <ShieldCheck size={11} className="text-emerald-700" /> {rawProductImg ? 'Verified Product' : 'Verified Deal'}
                                    </span>
                                    {offerDuration && offerDuration !== 'Life Time' && offerDuration !== 'Lifetime Deal' && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 bg-amber-50 text-amber-800 rounded-md border border-amber-200">
                                            <Clock size={11} className="text-amber-700" /> {offerDuration}
                                        </span>
                                    )}
                                </div>

                                <h2 className="text-lg sm:text-xl font-black text-slate-900 leading-snug tracking-tight">
                                    {discardHTMLTags(product?.offer_title)}
                                </h2>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Modal Content */}
                    <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-4 space-y-5 theme12-custom-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden bg-white">
                        {/* Price Breakdown Card */}
                        {(salePrice > 0 || hasDiscount) && (
                            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-0.5">
                                        Pricing & Value
                                    </span>
                                    <div className="flex items-baseline gap-2">
                                        {salePrice > 0 && (
                                            <span className="text-2xl font-black text-[#00a87a]">
                                                {currencySymbol}{salePrice.toFixed(2)}
                                            </span>
                                        )}
                                        {hasDiscount && (
                                            <span className="text-sm text-slate-400 line-through">
                                                {currencySymbol}{originalPrice.toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {hasDiscount && (
                                    <span className="px-3.5 py-1.5 bg-emerald-600 text-white text-xs font-black rounded-full shadow-xs">
                                        Save {currencySymbol}{savings} {discountPercent ? `(-${discountPercent}%)` : ''}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Coupon Code Section */}
                        {hasCoupon ? (
                            <div className="bg-gradient-to-br from-emerald-50/40 via-slate-50/40 to-transparent border-2 border-dashed border-emerald-300 p-5 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 shadow-2xs">
                                <div className="text-center sm:text-left">
                                    <span className="text-[10px] font-black text-[#00bf8d] uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1">
                                        <Tag size={12} className="text-[#00bf8d]" /> Coupon Code
                                    </span>
                                    <p className="text-2xl sm:text-3xl font-mono font-black text-slate-900 tracking-wider mt-1 select-all">
                                        {product.coupon_code}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    className={`w-full sm:w-auto px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-2 shadow-sm active:scale-95 cursor-pointer ${copied
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white'
                                        }`}
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                    {copied ? 'Copied!' : 'Copy Code'}
                                </button>
                            </div>
                        ) : (
                            <div className="p-4 bg-emerald-50/90 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-emerald-950 text-xs font-semibold">
                                <Sparkles size={16} className="text-emerald-700 shrink-0" />
                                <span>No code required! Discount is automatically applied when you visit store.</span>
                            </div>
                        )}

                        {/* Terms & Description with Expandable View */}
                        {cleanDetail && (
                            <div className="space-y-2 sm:space-y-2.5">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                    Offer Description & Terms
                                </h3>
                                <div className="bg-slate-50/80 p-4 sm:p-5 rounded-2xl border border-slate-100">
                                    <p className={`text-slate-800 text-xs sm:text-sm leading-relaxed ${isLongDetail && !isExpanded ? 'line-clamp-3' : ''
                                        }`}>
                                        {cleanDetail}
                                    </p>
                                    {isLongDetail && (
                                        <button
                                            type="button"
                                            onClick={() => setIsExpanded(!isExpanded)}
                                            className="mt-2.5 inline-flex items-center gap-1 text-xs font-bold text-[#00a87a] hover:underline transition-colors cursor-pointer group"
                                        >
                                            <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                                            <ChevronDown size={13} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* CTA Merchant Button */}
                        <div>
                            <button
                                onClick={() => {
                                    setDealLoading(true)
                                    setTimeout(() => {
                                        window.open(absoluteOutUrl, '_blank', 'noopener,noreferrer')
                                        setDealLoading(false)
                                    }, 700)
                                }}
                                disabled={dealLoading}
                                className="w-full h-12 sm:h-13 flex items-center justify-center gap-2.5 bg-[#071822] hover:bg-[#00d69f] text-white hover:text-[#071822] disabled:opacity-70 font-black rounded-xl transition-all duration-200 shadow-md shadow-[#071822]/15 active:scale-95 text-xs uppercase tracking-widest cursor-pointer group"
                            >
                                {dealLoading ? (
                                    <>Opening Store Site...</>
                                ) : (
                                    <>
                                        <ShoppingBag size={15} /> Visit Store & Redeem Offer <ExternalLink size={14} />
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Rating & Review Form */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3">
                            <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <MessageSquare size={14} className="text-[#00bf8d]" />
                                    <span className="text-xs font-black text-slate-800 uppercase tracking-wider">
                                        Rate & Review This Deal
                                    </span>
                                </div>
                                {hasSubmitted && (
                                    <span className="text-[9px] font-bold text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-200">
                                        Reviewed ✓
                                    </span>
                                )}
                            </div>

                            {/* Rating Stars */}
                            <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">
                                    Your Rating:
                                </span>
                                <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                                    {[1, 2, 3, 4, 5].map((starVal) => {
                                        const active = starVal <= (hoverRating || rating)
                                        return (
                                            <button
                                                key={starVal}
                                                type="button"
                                                disabled={hasSubmitted}
                                                onClick={() => setRating(starVal)}
                                                onMouseEnter={() => setHoverRating(starVal)}
                                                className="p-0.5 transition-transform hover:scale-125 disabled:hover:scale-100 cursor-pointer"
                                            >
                                                <Star
                                                    size={20}
                                                    className={`transition-colors ${active ? 'text-amber-400 fill-amber-400 drop-shadow-xs' : 'text-slate-300'}`}
                                                />
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Comment Input */}
                            <form onSubmit={handleCommentSubmit} className="space-y-2.5">
                                <textarea
                                    rows={2}
                                    placeholder={hasSubmitted ? "Review already submitted. Thank you!" : "Write a brief review about this deal..."}
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    disabled={hasSubmitted || submitting}
                                    className="w-full text-xs p-3 rounded-xl bg-white border border-slate-200 focus:border-[#0d2a3a] focus:ring-2 focus:ring-[#00bf8d]/15 outline-none resize-none text-slate-700 disabled:bg-slate-100"
                                />
                                <button
                                    type="submit"
                                    disabled={submitting || hasSubmitted}
                                    className="w-full py-2.5 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer"
                                >
                                    {submitting ? 'Submitting...' : hasSubmitted ? 'Submitted ✓' : <><Send size={13} /> Submit Review</>}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Footer Helpful Voting Bar */}
                    <div className="px-6 sm:px-8 py-3.5 bg-slate-50 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                        <span className="text-xs font-extrabold text-slate-600 uppercase tracking-wider">
                            Did this coupon work for you?
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleHelpful('yes')}
                                className={`px-4 py-1.5 rounded-full text-xs font-black flex items-center gap-1.5 transition-all duration-200 cursor-pointer ${helpfulVote === 'yes'
                                    ? 'bg-emerald-700 text-white shadow-sm'
                                    : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs active:scale-95'
                                    }`}
                            >
                                <ThumbsUp size={12} /> Yes, Worked
                            </button>
                            <button
                                onClick={() => handleHelpful('no')}
                                className={`px-4 py-1.5 rounded-full text-xs font-black flex items-center gap-1.5 transition-all duration-200 cursor-pointer ${helpfulVote === 'no'
                                    ? 'bg-rose-700 text-white shadow-sm'
                                    : 'bg-rose-600 hover:bg-rose-700 text-white shadow-xs active:scale-95'
                                    }`}
                            >
                                <ThumbsDown size={12} /> No
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )

    if (typeof window === 'undefined' || !document?.body) return null
    return createPortal(modalContent, document.body)
}

export default ProductCommentModal
